"use client";
import { EyeOutlined, SwapOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Tag } from "antd";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import * as godModeActions from "../../redux/godMode/reducer";
import { getGodModeSession, GodModeMode } from "../../util/godMode";
import "./style.css";

const {
    elevateGodModeSession,
    switchGodModeClient,
    refreshGodModeSession,
    setGodModeSessionState,
    resetGodModeOperationType,
} = godModeActions;

/** Where the God Mode picker lives. Leaving a client returns here, not to login. */
const PICKER_ROUTE = "/login/userSheet";

/** How often to re-check the session with the server: catches expiry and a mode
 *  change made in another tab without polling hard. */
const REFRESH_INTERVAL_MS = 60 * 1000;
const COUNTDOWN_INTERVAL_MS = 1000;

function formatRemaining(ms: number): string {
    if (ms <= 0) return "expired";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/**
 * God Mode banner.
 *
 * Always on screen while a session is open, because the single most dangerous
 * thing about impersonating a client is forgetting that you are. It shows which
 * center you are inside, whether the session can write, and how long is left.
 */
const GodModeBanner: React.FC = () => {
    const dispatch = useDispatch();
    const router = useRouter();
    const session = useSelector((state: any) => state.godMode?.session);
    const operationType = useSelector((state: any) => state.godMode?.operationType);
    const [remaining, setRemaining] = useState(0);
    const [elevateOpen, setElevateOpen] = useState(false);
    const [elevateReason, setElevateReason] = useState("");
    /** Guards the expiry handoff so a ticking clock fires it exactly once. */
    const expiredRef = useRef(false);

    /**
     * Mark the document while a session is open.
     *
     * The dashboard's topbar is `position: fixed; top: 0` via a CSS class, and it
     * is rendered twice — once by the layout wrapper and once inside Topbar
     * itself — so an inline offset on one of them leaves the other behind the
     * banner. A class on <body> shifts every .topbar-container at once.
     */
    useEffect(() => {
        if (typeof document === "undefined") return undefined;
        const active = !!session;
        document.body.classList.toggle("god-mode-active", active);
        return () => document.body.classList.remove("god-mode-active");
    }, [session]);

    // Rehydrate from storage on mount so a refresh keeps the banner up.
    useEffect(() => {
        const stored = getGodModeSession();
        if (stored && !session) {
            dispatch(setGodModeSessionState({ session: stored }));
        }
    }, []);

    useEffect(() => {
        if (!session) return undefined;
        expiredRef.current = false;

        const tick = () => {
            const left = Math.max(0, (session.expiresOn || 0) - Date.now());
            setRemaining(left);
            // Don't wait up to a minute for the next poll to notice: the moment the
            // clock runs out, leave the client. The server has already started
            // refusing this session anyway.
            if (left === 0 && !expiredRef.current) {
                expiredRef.current = true;
                dispatch(switchGodModeClient({ reason: "Session expired" }));
            }
        };
        tick();

        const countdown = setInterval(tick, COUNTDOWN_INTERVAL_MS);
        const refresh = setInterval(() => dispatch(refreshGodModeSession({})), REFRESH_INTERVAL_MS);
        return () => {
            clearInterval(countdown);
            clearInterval(refresh);
        };
    }, [session?.sessionId, session?.expiresOn]);

    // Covers the paths with no click behind them — an expired or revoked session
    // ends the same way. Harmless when the button handler has already routed.
    useEffect(() => {
        if (operationType !== "GOD_MODE_SWITCH_READY") return;
        dispatch(resetGodModeOperationType({}));
        router.push(PICKER_ROUTE);
    }, [operationType]);

    if (!session) return null;

    const isWriteMode = session.mode === GodModeMode.WRITE;

    // Teardown first, navigation second (the saga signals it with
    // GOD_MODE_SWITCH_READY). Routing ahead of the teardown mounts the picker
    // while this saga still has an authenticated call in flight, and their
    // silent-token requests then race each other — see teardownClientSession.
    const onSwitchClient = () => {
        dispatch(switchGodModeClient({}));
    };

    const onElevate = () => {
        if (elevateReason.trim().length < 5) return;
        dispatch(elevateGodModeSession({ reason: elevateReason.trim() }));
        setElevateOpen(false);
        setElevateReason("");
    };

    return (
        <>
            <div className={`god-mode-banner ${isWriteMode ? "god-mode-banner--write" : ""}`}>
                <div className="god-mode-banner__identity">
                    <span className="god-mode-banner__label">GOD MODE</span>
                    <span className="god-mode-banner__center">{session.centerName || session.centerId}</span>
                    <Tag color={isWriteMode ? "red" : "blue"} className="god-mode-banner__tag">
                        {isWriteMode ? (
                            <>
                                <ThunderboltOutlined /> Write enabled
                            </>
                        ) : (
                            <>
                                <EyeOutlined /> Read-only
                            </>
                        )}
                    </Tag>
                </div>

                <div className="god-mode-banner__actions">
                    <span className="god-mode-banner__timer">Expires in {formatRemaining(remaining)}</span>
                    {!isWriteMode && (
                        <Button size="small" danger onClick={() => setElevateOpen(true)}>
                            Take control
                        </Button>
                    )}
                    <Button
                        size="small"
                        type="primary"
                        icon={<SwapOutlined />}
                        onClick={onSwitchClient}
                    >
                        Switch client
                    </Button>
                </div>
            </div>

            <Modal
                title="Enable write mode"
                open={elevateOpen}
                onCancel={() => setElevateOpen(false)}
                onOk={onElevate}
                okText="Enable write mode"
                okButtonProps={{ danger: true, disabled: elevateReason.trim().length < 5 }}
            >
                <p>
                    You are about to make changes inside{" "}
                    <strong>{session.centerName || session.centerId}</strong>. Every change is
                    recorded in the God Mode audit trail against your account.
                </p>
                <Input.TextArea
                    rows={3}
                    placeholder="Why do you need to make changes? (e.g. ticket #1423 — fix duplicated invoice)"
                    value={elevateReason}
                    onChange={(event) => setElevateReason(event.target.value)}
                    maxLength={500}
                />
            </Modal>
        </>
    );
};

export default GodModeBanner;
